// The one way an address is edited and shown (shared/address.ts): the country first, then the fields its country has, in
// its order and its words (State and ZIP code in the US; District, City, Province in China; Post town and Postcode in
// the UK), a state or province picked from its list, the postal code with an example; "Paste an address…" splits a whole
// pasted address into them; what's still missing or wrong is said under them. Phone numbers: `phoneField()` shows
// them as their country writes them (+1 (215) 555-0134); the server saves them one way (E.164).
import { ClipboardPaste } from "lucide-react";
import { useState } from "react";
import { type Address, addressFields, addressLine, addressProblems, COUNTRIES, countryName, normalizeAddress, parseAddress } from "../../../shared/address.ts";
import { phoneText } from "../../../shared/phone.ts";
import { Button, Textarea } from "./controls.tsx";
import { Popover } from "./Popover.tsx";
import type { FieldDef } from "./RecordPanel.tsx";

const COUNTRY_OPTIONS = [{ value: "", label: "Pick its country…" }, ...COUNTRIES.map((c) => ({ value: c.code, label: c.name }))];

/** A row's address from its columns (`prefix`: a location's `address_`). */
export function addressOfRow(row: object, prefix = ""): Address {
  const s = (k: string) => String((row as Record<string, unknown>)[`${prefix}${k}`] ?? "");
  return {
    line1: s("line1"),
    line2: s("line2"),
    district: s("district"),
    city: s("city"),
    region: s("region"),
    postal_code: s("postal_code"),
    country: s("country"),
  };
}

/** The panel fields for an address: its country, then its country's own (for `Fields`; `prefix` for a location's). */
export function addressFieldDefs(country: string, prefix = ""): FieldDef[] {
  return [
    { key: `${prefix}country`, label: "Country", type: "select", options: COUNTRY_OPTIONS, format: (v) => (v ? countryName(String(v)) : "") },
    ...addressFields(country || "ZZ").map(
      (f): FieldDef => ({
        key: `${prefix}${f.key}`,
        label: f.label,
        ...(f.options ? { type: "select" as const, options: [{ value: "", label: `Pick its ${f.label.toLowerCase()}…` }, ...f.options] } : {}),
        ...(f.placeholder ? { placeholder: `e.g. ${f.placeholder}` } : {}),
        ...(f.key === "postal_code" ? { mono: true } : {}),
      }),
    ),
  ];
}

/** A phone field shown as its country writes it; what's typed is saved one way by the server (E.164). */
export function phoneField(key: string, label = "Phone"): FieldDef {
  return { key, label, placeholder: "e.g. +86 138 0013 8000", format: (v) => phoneText(String(v ?? "")) };
}

/** What's still missing or wrong with an address, in a line under its fields (nothing when it's right, or empty). */
export function AddressHints({ address }: { address: Address }) {
  const problems = addressProblems(normalizeAddress(address));
  if (!problems.length) return null;
  return <div className="px-2 pt-1 text-[11px] text-warn">{problems.join(" · ")}</div>;
}

/**
 * "Paste an address…": a whole address from an email or a PI, split into its fields (shown before it's used), then
 * saved in one go by `onApply` (all the fields, so the old ones don't linger). `hint`: the country it's most likely in.
 */
export function PasteAddress({ hint, onApply, disabled }: { hint?: string; onApply: (a: Address) => void; disabled?: boolean }) {
  const [text, setText] = useState("");
  const parsed = text.trim() ? parseAddress(text, hint) : null;
  return (
    <Popover
      width={420}
      button={(toggle) => (
        <Button size="sm" variant="ghost" icon={<ClipboardPaste className="size-3.5" />} onClick={toggle} disabled={disabled}>
          Paste an address…
        </Button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-2 p-3 text-[12px]">
          {/* The buttons on top: the preview below may run past the window's edge */}
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 font-medium">Paste the whole address</div>
            <Button size="sm" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!parsed}
              onClick={() => {
                if (!parsed) return;
                onApply(parsed.address);
                setText("");
                close();
              }}
            >
              Use it
            </Button>
          </div>
          <Textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"e.g. No. 5 Xinhe Road, Bao'an District, Shenzhen, Guangdong, China 518101"}
            className="min-h-20 text-[12px]"
          />
          {parsed && (
            <div className="rounded-md border border-line bg-surface-2/50 p-2">
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                {[
                  ["Country", parsed.address.country ? countryName(parsed.address.country) : ""],
                  ...addressFields(parsed.address.country || "ZZ").map((f) => [f.label, parsed.address[f.key]] as const),
                ].map(([label, value]) => (
                  <div key={label} className="contents">
                    <span className="text-muted">{label}</span>
                    <span className={value ? "" : "text-faint"}>{value || "—"}</span>
                  </div>
                ))}
              </div>
              {parsed.left.length > 0 && <div className="mt-1.5 text-[11px] text-warn">{parsed.left.join(" · ")}</div>}
            </div>
          )}
        </div>
      )}
    </Popover>
  );
}

/** An address on one line, as its country writes it ("No. 5 Xinhe Road, Bao'an District, Shenzhen, Guangdong, 518101, China"). */
export const addressText = (a: Address) => addressLine(normalizeAddress(a));
